import React, { useEffect, useState } from 'react';
import { X, Check } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabaseClient';
interface PrivacySettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}
export function PrivacySettingsModal({
  isOpen,
  onClose
}: PrivacySettingsModalProps) {
  const {
    user,
    refreshUser
  } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [settings, setSettings] = useState({
    dataSharing: false,
    profileVisibility: 'private' as 'public' | 'private',
    analyticsTracking: true,
    marketingEmails: false,
    scanHistoryRetention: '90' as '30' | '90' | '180' | 'forever'
  });
  useEffect(() => {
    if (isOpen && user) {
      loadPrivacySettings();
    }
  }, [isOpen, user]);
  const loadPrivacySettings = () => {
    const privacySettings = user?.user_metadata?.privacySettings || {};
    setSettings({
      dataSharing: privacySettings.dataSharing || false,
      profileVisibility: privacySettings.profileVisibility || 'private',
      analyticsTracking: privacySettings.analyticsTracking ?? true,
      marketingEmails: privacySettings.marketingEmails || false,
      scanHistoryRetention: privacySettings.scanHistoryRetention || '90'
    });
  };
  const handleSave = async () => {
    setLoading(true);
    setError(null);
    try {
      const {
        error: updateError
      } = await supabase.auth.updateUser({
        data: {
          ...user?.user_metadata,
          privacySettings: settings
        }
      });
      if (updateError) throw updateError;
      await refreshUser();
      setSuccess(true);
      setTimeout(() => {
        onClose();
        setSuccess(false);
      }, 2000);
    } catch (err: any) {
      setError(err.message || 'Failed to update privacy settings');
    } finally {
      setLoading(false);
    }
  };
  if (!isOpen) return null;
  return <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-1867">
      <div className="bg-white rounded-xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto" data-id="element-1868">
        <div className="flex items-center justify-between mb-6" data-id="element-1869">
          <h2 className="text-xl font-bold" data-id="element-1870">Privacy Settings</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg" data-id="element-1871">
            <X size={20} data-id="element-1872" />
          </button>
        </div>
        {success ? <div className="text-center py-8" data-id="element-1873">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-1874">
              <Check size={32} className="text-green-600" data-id="element-1875" />
            </div>
            <h3 className="text-lg font-semibold mb-2" data-id="element-1876">Settings Saved!</h3>
            <p className="text-gray-600 text-sm" data-id="element-1877">
              Your privacy preferences have been updated
            </p>
          </div> : <div className="space-y-6" data-id="element-1878">
            {/* Data Sharing */}
            <div className="border-b border-gray-200 pb-4" data-id="element-1879">
              <div className="flex items-center justify-between mb-2" data-id="element-1880">
                <div data-id="element-1881">
                  <h3 className="font-medium text-sm" data-id="element-1882">Data Sharing</h3>
                  <p className="text-xs text-gray-500 mt-1" data-id="element-1883">
                    Allow anonymous usage data collection for product
                    improvement
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer" data-id="element-1884">
                  <input type="checkbox" checked={settings.dataSharing} onChange={e => setSettings({
                ...settings,
                dataSharing: e.target.checked
              })} className="sr-only peer" data-id="element-1885" />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600" data-id="element-1886"></div>
                </label>
              </div>
            </div>
            {/* Profile Visibility */}
            <div className="border-b border-gray-200 pb-4" data-id="element-1887">
              <h3 className="font-medium text-sm mb-3" data-id="element-1888">Profile Visibility</h3>
              <div className="space-y-2" data-id="element-1889">
                <label className="flex items-center" data-id="element-1890">
                  <input type="radio" name="profileVisibility" checked={settings.profileVisibility === 'public'} onChange={() => setSettings({
                ...settings,
                profileVisibility: 'public'
              })} className="mr-2" data-id="element-1891" />
                  <span className="text-sm" data-id="element-1892">Public</span>
                </label>
                <label className="flex items-center" data-id="element-1893">
                  <input type="radio" name="profileVisibility" checked={settings.profileVisibility === 'private'} onChange={() => setSettings({
                ...settings,
                profileVisibility: 'private'
              })} className="mr-2" data-id="element-1894" />
                  <span className="text-sm" data-id="element-1895">Private</span>
                </label>
              </div>
            </div>
            {/* Analytics Tracking */}
            <div className="border-b border-gray-200 pb-4" data-id="element-1896">
              <div className="flex items-center justify-between mb-2" data-id="element-1897">
                <div data-id="element-1898">
                  <h3 className="font-medium text-sm" data-id="element-1899">Analytics Tracking</h3>
                  <p className="text-xs text-gray-500 mt-1" data-id="element-1900">
                    Help us improve by tracking your usage patterns
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer" data-id="element-1901">
                  <input type="checkbox" checked={settings.analyticsTracking} onChange={e => setSettings({
                ...settings,
                analyticsTracking: e.target.checked
              })} className="sr-only peer" data-id="element-1902" />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600" data-id="element-1903"></div>
                </label>
              </div>
            </div>
            {/* Marketing Emails */}
            <div className="border-b border-gray-200 pb-4" data-id="element-1904">
              <div className="flex items-center justify-between mb-2" data-id="element-1905">
                <div data-id="element-1906">
                  <h3 className="font-medium text-sm" data-id="element-1907">Marketing Emails</h3>
                  <p className="text-xs text-gray-500 mt-1" data-id="element-1908">
                    Receive promotional emails and product updates
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer" data-id="element-1909">
                  <input type="checkbox" checked={settings.marketingEmails} onChange={e => setSettings({
                ...settings,
                marketingEmails: e.target.checked
              })} className="sr-only peer" data-id="element-1910" />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600" data-id="element-1911"></div>
                </label>
              </div>
            </div>
            {/* Scan History Retention */}
            <div className="pb-4" data-id="element-1912">
              <h3 className="font-medium text-sm mb-3" data-id="element-1913">
                Scan History Retention
              </h3>
              <select value={settings.scanHistoryRetention} onChange={e => setSettings({
            ...settings,
            scanHistoryRetention: e.target.value as any
          })} className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-600 focus:border-transparent" data-id="element-1914">
                <option value="30" data-id="element-1915">30 days</option>
                <option value="90" data-id="element-1916">90 days</option>
                <option value="180" data-id="element-1917">180 days</option>
                <option value="forever" data-id="element-1918">Forever</option>
              </select>
              <p className="text-xs text-gray-500 mt-2" data-id="element-1919">
                How long to keep your scan history before automatic deletion
              </p>
            </div>
            {error && <div className="p-3 rounded-lg text-sm bg-red-50 text-red-800 border border-red-200" data-id="element-1920">
                {error}
              </div>}
            <div className="flex gap-3 pt-2" data-id="element-1921">
              <button type="button" onClick={onClose} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg font-medium hover:bg-gray-50 transition-colors" data-id="element-1922">
                Cancel
              </button>
              <button onClick={handleSave} disabled={loading} className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-1923">
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>}
      </div>
    </div>;
}